/**
 * Catalyst Web SDK helpers — the ONE place the app talks to window.catalyst.
 *
 * `sdkSession()` is the same-origin source of truth for "does a Catalyst session
 * exist". It is what breaks the login loop: only show the embedded login when
 * the SDK DEFINITIVELY reports no session (mounting signIn() over a live session
 * makes the SDK short-circuit into auth/signin-redirect -> reload -> loop).
 */

// Resolve `null` if the SDK never loads (plain `react-scripts start` — init.js
// 404s) instead of throwing, so callers can fall back to proxy/cookie behaviour.
export function waitForCatalyst(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(null);
    if (window.catalyst) return resolve(window.catalyst);
    const deadline = Date.now() + timeoutMs;
    const timer = setInterval(() => {
      if (window.catalyst) {
        clearInterval(timer);
        resolve(window.catalyst);
      } else if (Date.now() > deadline) {
        clearInterval(timer);
        resolve(null);
      }
    }, 200);
  });
}

/**
 * Same-origin Catalyst session check. Never throws.
 * Returns { available, authenticated, user }:
 *   available     — the SDK loaded at all (false in plain CRA dev),
 *   authenticated — a Catalyst session exists,
 *   user          — raw Catalyst payload (email_id, first_name, role_details, ...).
 */
export async function sdkSession() {
  const sdk = await waitForCatalyst();
  if (!sdk?.auth?.isUserAuthenticated) {
    return { available: false, authenticated: false, user: null };
  }
  try {
    const result = await sdk.auth.isUserAuthenticated();
    if (result?.content) return { available: true, authenticated: true, user: result.content };
    return { available: true, authenticated: false, user: null };
  } catch {
    return { available: true, authenticated: false, user: null };
  }
}

/**
 * Public signup. Requires "Public Signup" enabled in the Catalyst console.
 * Catalyst sends a verification email; the account is usable only after the
 * user verifies and sets a password. Returns the SDK response ({ status, ... }).
 */
export async function signUp({ firstName, lastName, email }) {
  const sdk = await waitForCatalyst();
  if (!sdk?.auth?.signUp) throw new Error('Catalyst SDK not available');
  return sdk.auth.signUp({
    first_name: firstName,
    last_name: lastName,
    email_id: email,
    platform_type: 'web',
  });
}
